// ============================================================================
// BESPOKIAN Health - Registration Page
// New patient signup with luxury styling
// ============================================================================

import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Shield, Eye, EyeOff, AlertCircle, Check, User, Mail, Phone, Lock } from 'lucide-react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';

export function RegisterPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false,
    agreedToHIPAA: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.agreedToTerms || !formData.agreedToHIPAA) {
      setError('Please agree to the Terms of Service and HIPAA Notice');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Implement actual registration API call
      // await authApi.register(formData);
      
      // For now, redirect to login
      setTimeout(() => {
        navigate({ to: '/login' });
      }, 1500);
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    'Transparent pricing on all medications',
    'Pharmacist support available',
    'Free shipping on orders $50+',
    'Easy prescription transfers',
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Benefits Section */}
            <div className="lg:sticky lg:top-32">
              <div className="w-12 h-0.5 bg-gold-500 mb-6" />
              <h1 className="font-display text-3xl md:text-4xl text-navy-800 tracking-tight italic mb-4">
                Join BESPOKIAN Health
              </h1>
              <p className="text-navy-600 text-lg mb-8">
                Create your account and start saving on your prescriptions today.
              </p>

              <div className="space-y-4 mb-8">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-sage-600" />
                    </div>
                    <span className="text-navy-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-navy-50 rounded-xl p-6 border border-navy-100">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="h-5 w-5 text-sage-600" />
                  <span className="font-semibold text-navy-800">Your Privacy Protected</span>
                </div>
                <p className="text-navy-600 text-sm">
                  Your health information is protected by HIPAA regulations. 
                  We use bank-level encryption to keep your data secure.
                </p>
              </div>
            </div>

            {/* Registration Form */}
            <div className="bg-white rounded-xl border border-navy-100 shadow-card overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-400" />
              <div className="p-8">
                <h2 className="font-display text-xl text-navy-800 tracking-tight mb-6">Create Your Account</h2>

                {error && (
                  <div className="mb-6 p-4 bg-coral-50 border border-coral-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-coral-600 flex-shrink-0 mt-0.5" />
                    <span className="text-coral-700">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-navy-700 mb-1.5">
                        First Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-navy-300" />
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                          placeholder="John"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-navy-700 mb-1.5">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-navy-300" />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-navy-300" />
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-navy-300" />
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-10 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                        placeholder="Min. 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-navy-700 mb-1.5">
                      Confirm Password
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-white text-navy-800 placeholder:text-navy-300"
                      placeholder="Re-enter password"
                    />
                  </div>

                  {/* Agreements */}
                  <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agreedToTerms"
                        checked={formData.agreedToTerms}
                        onChange={handleChange}
                        className="h-5 w-5 rounded border-navy-300 text-gold-600 focus:ring-gold-500 mt-0.5"
                      />
                      <span className="text-sm text-navy-600">
                        I agree to the{' '}
                        <Link to="/terms" className="text-gold-600 hover:text-gold-700 font-medium">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-gold-600 hover:text-gold-700 font-medium">
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agreedToHIPAA"
                        checked={formData.agreedToHIPAA}
                        onChange={handleChange}
                        className="h-5 w-5 rounded border-navy-300 text-gold-600 focus:ring-gold-500 mt-0.5"
                      />
                      <span className="text-sm text-navy-600">
                        I acknowledge the{' '}
                        <Link to="/hipaa" className="text-gold-600 hover:text-gold-700 font-medium">
                          HIPAA Notice of Privacy Practices
                        </Link>
                      </span>
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>

                {/* Login Link */}
                <div className="mt-6 pt-6 border-t border-navy-100 text-center">
                  <p className="text-navy-600 text-sm">
                    Already have an account?{' '}
                    <Link to="/login" className="text-gold-600 hover:text-gold-700 font-medium">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
